import { useEffect, useState } from "react";
import { IMyPortfolio } from "../../../../interface/myPortfolio";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { formatCurrency, formatNumberWithDecimal, getCurrentFinancialYear } from "../../../../utils/commonUtil";

interface ICashFlowSummary {
  street: string;
  suburb: string;
  state: string;
  postcode: string;
  country: string;
  type: string;
  preTaxCashflow?: number;
  taxableProfitLoss?: number;
  yeildPurchasePrice?: number;
  yeildCurrentValue?: number;
}

const CashFlowSummary: React.FC<{
  cashFlowData: IMyPortfolio;
  year: string;
  isShowingTotal?: boolean;
}> = ({ cashFlowData, year, isShowingTotal }) => {
  const [transformedData, setTransformedData] = useState<ICashFlowSummary[]>([]);
  const currentYear = "20" + Number(getCurrentFinancialYear()).toString();

  useEffect(() => {
    if (cashFlowData) {
      const actual = isShowingTotal
        ? cashFlowData.CashflowInDetail.CashflowSummary?.Actuals?.find(
            (item: any) => item.Year === year && Number(year) <= Number(currentYear)
          )
        : cashFlowData.CashflowInDetail.CashflowSummaryPortions?.Actuals?.find(
            (item: any) => item.Year === year && Number(year) <= Number(currentYear)
          ) || {};
      const forecast = isShowingTotal
        ? cashFlowData.CashflowInDetail.CashflowSummary?.Forecast?.find((item: any) => item.Year === year) || {}
        : cashFlowData.CashflowInDetail.CashflowSummaryPortions?.Forecast?.find((item: any) => item.Year === year) ||
          {};
      const variance = isShowingTotal
        ? cashFlowData.CashflowInDetail.CashflowSummary?.Variance?.find(
            (item: any) => item.Year === year && Number(year) <= Number(currentYear)
          )
        : cashFlowData.CashflowInDetail.CashflowSummaryPortions?.Variance?.find(
            (item: any) => item.Year === year && Number(year) <= Number(currentYear)
          ) || {};

      setTransformedData(
        [
          { type: "Forecast", data: forecast },
          { type: "Actuals", data: actual },
          { type: "Variance", data: variance },
        ].map(
          ({
            type,
            data,
          }: {
            type: string;
            data: { PreTaxCashflow?: number; NetProfit?: number; YieldVsPurchase?: number; YieldVsValuation?: number };
          }) => ({
            street: cashFlowData.PortfolioTrackerDetailLabel.StreetAddress || "",
            suburb: cashFlowData.PortfolioTrackerDetailLabel.Suburb || "",
            state: cashFlowData.PortfolioTrackerDetailLabel.State || "",
            postcode: cashFlowData.PortfolioTrackerDetailLabel?.Postcode || "",
            country: cashFlowData.PortfolioTrackerDetailLabel?.Country || "",
            type,
            preTaxCashflow: Number(data?.PreTaxCashflow) || 0,
            taxableProfitLoss: Number(data?.NetProfit) || 0,
            yeildPurchasePrice: Number(data?.YieldVsPurchase) || 0,
            yeildCurrentValue: Number(data?.YieldVsValuation) || 0,
          })
        )
      );
    }
  }, [cashFlowData, year, isShowingTotal]);

  const currencyTemplate = (value: number) => (
    <span className={value < 0 ? "text-red-600" : ""}>
      {value < 0 ? `(${formatCurrency(-value)})` : formatCurrency(Number(value))}
    </span>
  );

  const percentageTemplate = (value: number) => (
    <span className={value < 0 ? "text-red-600" : ""}>
      {value < 0 ? `(${formatNumberWithDecimal(-value, 2, 2)}%)` : `${formatNumberWithDecimal(value, 2, 2)}%`}
    </span>
  );

  return (
    transformedData && (
      <DataTable
        value={transformedData}
        rowGroupMode="rowspan"
        groupRowsBy="street"
        sortField="street"
        sortMode="single"
        sortOrder={1}
        size="small"
      >
        <Column
          field="street"
          header="Address/property name"
          body={(rowData) => (
            <div className="flex items-center">
              <span>{`${rowData.street}, ${rowData.suburb}, ${rowData.state}, ${rowData.postcode} (${rowData.country})`}</span>
            </div>
          )}
        />
        <Column field="type" header="Type" body={(rowData) => rowData.type} />
        <Column
          field="preTaxCashflow"
          header="Pre-tax cash flow"
          body={(rowData) =>
            rowData.preTaxCashflow
              ? currencyTemplate(rowData.preTaxCashflow)
              : Number(year) <= Number(currentYear)
              ? "$0"
              : ""
          }
          align="right"
        />
        <Column
          field="taxableProfitLoss"
          header="Taxable profit/loss"
          body={(rowData) =>
            rowData.taxableProfitLoss
              ? currencyTemplate(rowData.taxableProfitLoss)
              : Number(year) <= Number(currentYear)
              ? "$0"
              : ""
          }
          align="right"
        />
        <Column
          field="yeildPurchasePrice"
          header="Yield % of purchase price"
          body={(rowData) =>
            rowData.yeildPurchasePrice
              ? percentageTemplate(rowData.yeildPurchasePrice)
              : Number(year) <= Number(currentYear)
              ? "0.00%"
              : ""
          }
          align="right"
        />
        <Column
          field="yeildCurrentValue"
          header="Yield % of current value"
          body={(rowData) =>
            rowData.yeildCurrentValue
              ? percentageTemplate(rowData.yeildCurrentValue)
              : Number(year) <= Number(currentYear)
              ? "0.00%"
              : ""
          }
          align="right"
        />
      </DataTable>
    )
  );
};

export default CashFlowSummary;
